/**
 * ASSIST-01 — `useAssistMode()`: is Assist on, and is this viewer a tester?
 *
 * Returns `{ mode, isTester, resolved }` (plus the config and the viewer id).
 * Nothing Assist renders before `resolved` (brief R2a.5, R18.4):
 *   - on the server it never resolves (effects do not run during SSR), so the
 *     property page's cached HTML carries no Assist markup;
 *   - on the client it resolves after mount, so the first client render
 *     matches the server's and hydration is clean;
 *   - loading is not `off`: a guard waits instead of redirecting a tester.
 * Any failure resolves to `off` (R1).
 *
 * One request per viewer per page session. The cache is keyed by the user id
 * in the access token (`anon` for guests), so signing in or out reads again.
 * A failed request is not cached; the next mount retries.
 *
 * `viewerKey` is optional: hosts with an AuthProvider pass `user?.id` so the
 * hook re-reads when the signed-in user changes. The Vike property page has no
 * AuthProvider and passes nothing; the token is read either way.
 */
import { useEffect, useState } from 'react';
import { getValidAccessToken } from '../lib/api/authApi';
import { fetchAssistPublicConfig } from '../lib/api/assistApi';
import {
  ASSIST_OFF_CONFIG,
  type AssistMode,
  type AssistPublicConfig,
} from '../src/lib/assist/config';
import { jwtSubject } from '../src/lib/assist/viewer';

export interface AssistModeState {
  mode: AssistMode;
  isTester: boolean;
  resolved: boolean;
  config: AssistPublicConfig;
  /** The signed-in user's id, or null for a guest. */
  viewerId: string | null;
}

interface Resolution {
  config: AssistPublicConfig;
  viewerId: string | null;
}

const UNRESOLVED: AssistModeState = {
  mode: 'off',
  isTester: false,
  resolved: false,
  config: ASSIST_OFF_CONFIG,
  viewerId: null,
};

const configByViewer = new Map<string, Promise<AssistPublicConfig | null>>();

async function resolveAssistMode(): Promise<Resolution> {
  let token: string | null = null;
  try {
    token = await getValidAccessToken();
  } catch {
    token = null;
  }
  const viewerId = jwtSubject(token);
  const key = viewerId ?? 'anon';

  let request = configByViewer.get(key);
  if (!request) {
    request = fetchAssistPublicConfig(viewerId ? token : null);
    configByViewer.set(key, request);
    request.then((config) => {
      if (!config) configByViewer.delete(key);
    });
  }
  const config = (await request) ?? ASSIST_OFF_CONFIG;
  return { config, viewerId };
}

export function useAssistMode(viewerKey?: string | null): AssistModeState {
  const [state, setState] = useState<{ key: string | null | undefined; value: AssistModeState }>({
    key: viewerKey,
    value: UNRESOLVED,
  });

  useEffect(() => {
    let cancelled = false;
    resolveAssistMode()
      .catch((): Resolution => ({ config: ASSIST_OFF_CONFIG, viewerId: null }))
      .then(({ config, viewerId }) => {
        if (cancelled) return;
        setState({
          key: viewerKey,
          value: {
            mode: config.mode,
            isTester: config.isTester,
            resolved: true,
            config,
            viewerId,
          },
        });
      });
    return () => {
      cancelled = true;
    };
  }, [viewerKey]);

  // A resolution made for a different viewer is not a resolution for this one.
  return state.key === viewerKey ? state.value : UNRESOLVED;
}
